import React, { useState } from 'react';
import Image from 'components/AppImage';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

function StarRating({ rating, count, large }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5]?.map((star) => (
        <Icon key={star} name="Star" size={large ? 16 : 12}
          color={star <= Math.round(rating) ? 'var(--color-accent)' : 'var(--color-border)'}
          style={{ fill: star <= Math.round(rating) ? 'var(--color-accent)' : 'transparent' }} />
      ))}
      <span className={`font-caption ml-1 ${large ? 'text-sm' : 'text-xs'}`} style={{ color: 'var(--color-muted-foreground)' }}>
        {rating} ({count} reviews)
      </span>
    </div>
  );
}

export default function ProductDetailModal({ product, onClose, onAddToCart, isInCart }) {
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [addedAnim, setAddedAnim] = useState(false);

  if (!product) return null;

  const handleAdd = () => {
    onAddToCart(product, qty);
    setAddedAnim(true);
    setTimeout(() => setAddedAnim(false), 1500);
  };

  const images = product?.images || [product?.image];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,48,87,0.7)' }}
      onClick={onClose}>
      <div
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
        style={{ background: 'var(--color-background)' }}
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Close */}
        <button onClick={onClose} className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: 'var(--color-muted)' }}>
          <Icon name="X" size={18} color="var(--color-foreground)" />
        </button>

        <div className="flex flex-col md:flex-row gap-0">
          {/* Images */}
          <div className="md:w-1/2 p-4 md:p-6">
            <div className="rounded-xl overflow-hidden h-64 md:h-80 mb-3">
              <Image src={images?.[activeImg]} alt={product?.imageAlt} className="w-full h-full object-cover" />
            </div>
            {images?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {images?.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)}
                    className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all"
                    style={{ borderColor: activeImg === i ? 'var(--color-accent)' : 'var(--color-border)' }}>
                    <Image src={img} alt={`Product view ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="md:w-1/2 p-4 md:p-6 flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-caption text-xs px-2 py-0.5 rounded-full"
                style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)' }}>
                {product?.category}
              </span>
              {product?.winVerified && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-caption text-xs font-medium"
                  style={{ background: 'rgba(133,113,77,0.12)', color: 'var(--color-accent)' }}>
                  <Icon name="Crown" size={10} color="var(--color-accent)" />WIN Verified
                </span>
              )}
            </div>

            <h2 className="font-heading font-bold text-2xl mb-1" style={{ color: 'var(--color-foreground)' }}>
              {product?.name}
            </h2>

            {/* Seller */}
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-full overflow-hidden">
                <Image src={product?.sellerAvatar} alt={product?.sellerAvatarAlt} className="w-full h-full object-cover" />
              </div>
              <div>
                <span className="font-caption text-sm font-medium" style={{ color: 'var(--color-primary)' }}>{product?.seller}</span>
                <span className="font-caption text-xs ml-1" style={{ color: 'var(--color-muted-foreground)' }}>· Women-Owned</span>
              </div>
            </div>

            <StarRating rating={product?.rating} count={product?.reviewCount} large />

            <p className="font-body text-sm mt-3 mb-4 leading-relaxed" style={{ color: 'var(--color-muted-foreground)' }}>
              {product?.fullDescription || product?.description}
            </p>

            <div className="flex items-center gap-3 mb-4">
              <span className="font-data font-bold text-3xl" style={{ color: 'var(--color-foreground)' }}>
                ${product?.price?.toFixed(2)}
              </span>
              {product?.originalPrice && (
                <span className="font-data text-lg line-through" style={{ color: 'var(--color-muted-foreground)' }}>
                  ${product?.originalPrice?.toFixed(2)}
                </span>
              )}
            </div>

            {/* Qty */}
            <div className="flex items-center gap-3 mb-4">
              <span className="font-caption text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>Qty:</span>
              <div className="flex items-center border rounded-lg overflow-hidden" style={{ borderColor: 'var(--color-border)' }}>
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-9 flex items-center justify-center transition-colors hover:bg-gray-100">
                  <Icon name="Minus" size={14} color="var(--color-foreground)" />
                </button>
                <span className="w-10 text-center font-data text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="w-9 h-9 flex items-center justify-center transition-colors hover:bg-gray-100">
                  <Icon name="Plus" size={14} color="var(--color-foreground)" />
                </button>
              </div>
            </div>

            <div className="flex gap-3 mt-auto">
              <Button variant="default" fullWidth onClick={handleAdd} iconName={addedAnim ? 'Check' : 'ShoppingCart'} iconPosition="left">
                {addedAnim ? 'Added to Cart!' : isInCart ? 'Add More' : 'Add to Cart'}
              </Button>
              <Button variant="outline" size="default" iconName="Bookmark" iconPosition="left">Save</Button>
            </div>

            {/* Tags */}
            {product?.tags && (
              <div className="flex flex-wrap gap-1.5 mt-4">
                {product?.tags?.map(tag => (
                  <span key={tag} className="font-caption text-xs px-2 py-0.5 rounded-full"
                    style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)' }}>
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Reviews */}
        <div className="px-4 md:px-6 pb-6">
          <h3 className="font-heading font-semibold text-lg mb-4" style={{ color: 'var(--color-foreground)', borderTop: '1px solid var(--color-border)', paddingTop: '1rem' }}>
            Customer Reviews
          </h3>
          <div className="space-y-4">
            {product?.reviews?.map((review, i) => (
              <div key={i} className="flex gap-3">
                <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                  <Image src={review?.avatar} alt={review?.avatarAlt} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-caption text-sm font-semibold" style={{ color: 'var(--color-foreground)' }}>{review?.name}</span>
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5]?.map(s => (
                        <Icon key={s} name="Star" size={11} color={s <= review?.rating ? 'var(--color-accent)' : 'var(--color-border)'}
                          style={{ fill: s <= review?.rating ? 'var(--color-accent)' : 'transparent' }} />
                      ))}
                    </div>
                    <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>{review?.date}</span>
                  </div>
                  <p className="font-caption text-sm" style={{ color: 'var(--color-muted-foreground)' }}>{review?.comment}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}